import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { useLanguage } from "../context/LanguageContext";
import { useSpaData } from "../context/SpaDataContext";
import { PHONE_DISPLAY, EMAIL, ADDRESS, MAPS_LINK, waLink } from "../lib/spa";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { PaymentMethods } from "./PaymentMethods";

export const Footer = () => {
  const { t, ml } = useLanguage();
  const { massages } = useSpaData();

  return (
    <footer className="border-t border-foreground/5 bg-secondary">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-4">
            <Logo />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-foreground/55">{t("footer.tagline")}</p>
            <div className="mt-6">
              <h4 className="mb-3 text-xs uppercase tracking-[0.3em] text-gold">{t("footer.paymentsTitle")}</h4>
              <PaymentMethods />
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">{t("footer.navigation")}</h4>
            <ul className="space-y-2.5 text-sm text-foreground/65">
              <li><Link to="/" className="transition-colors hover:text-foreground">{t("nav.home")}</Link></li>
              <li><Link to="/terapeutas" className="transition-colors hover:text-foreground">{t("nav.therapists")}</Link></li>
              <li><Link to="/massagens" className="transition-colors hover:text-foreground">{t("nav.massages")}</Link></li>
              <li><Link to="/sobre" className="transition-colors hover:text-foreground">{t("nav.about")}</Link></li>
              <li>
                <Link to="/precos" data-testid="footer-prices-link" className="transition-colors hover:text-foreground">
                  {t("footer.prices")}
                </Link>
              </li>
              <li>
                <Link to="/promocoes" data-testid="footer-promotions-link" className="transition-colors hover:text-foreground">
                  {t("footer.promotions")}
                </Link>
              </li>
              <li>
                <Link to="/candidaturas" data-testid="footer-applications-link" className="transition-colors hover:text-foreground">
                  {t("footer.applications")}
                </Link>
              </li>
              <li><Link to="/contactos" className="transition-colors hover:text-foreground">{t("nav.contact")}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">{t("footer.massages")}</h4>
            <ul className="space-y-2.5 text-sm text-foreground/65">
              {massages.slice(0, 5).map((m) => (
                <li key={m.slug}>
                  <Link to={`/massagens/${m.slug}`} className="transition-colors hover:text-foreground">
                    {ml(m.name)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">{t("footer.contact")}</h4>
            <ul className="space-y-3 text-sm text-foreground/65">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold/60" strokeWidth={1.5} />
                <a
                  href={MAPS_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-address-link"
                  className="transition-colors hover:text-foreground"
                >
                  {ADDRESS}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-whatsapp-link"
                  href={waLink(t("booking.generalMessage"))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 transition-colors hover:text-foreground"
                >
                  <Phone className="h-4 w-4 text-gold/60" strokeWidth={1.5} />
                  {PHONE_DISPLAY}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-gold/60" strokeWidth={1.5} />
                <a href={`mailto:${EMAIL}`} className="transition-colors hover:text-foreground">{EMAIL}</a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-gold/60" strokeWidth={1.5} />
                {t("footer.hours")}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-foreground/5 pt-8 text-xs text-foreground/45 sm:flex-row">
          <span>© {new Date().getFullYear()} Magnolia Lunar. {t("footer.rights")}</span>
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/privacidade" className="transition-colors hover:text-gold">
              {t("footer.privacy")}
            </Link>
            <Link to="/cookies" className="transition-colors hover:text-gold">
              {t("footer.cookies")}
            </Link>
            <Link to="/termos" className="transition-colors hover:text-gold">
              {t("footer.terms")}
            </Link>
            <span aria-hidden="true" className="hidden text-foreground/20 sm:inline">
              •
            </span>
            <span className="text-foreground/40">{t("footer.disclaimer")}</span>
          </nav>
        </div>
      </div>
    </footer>
  );
};
